/**
 * Interface translations.
 *
 * Four languages, because the people sitting this exam are foreigners living
 * in Poland: Polish, Ukrainian, Russian and English. The exam content itself is never
 * translated — tasks, instructions and answer options stay in Polish, exactly
 * as they are on the real paper. Translating them would defeat the point.
 *
 * No i18n library: a typed dictionary and a context are enough here, and
 * `Messages` being derived from the Polish catalogue means TypeScript refuses
 * to compile a translation with a missing key.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from "react";
import type { ReactNode } from "react";
import { currentLocale, currentPath, goTo, localize, useUrlLocale } from "@/lib/router";
import { track } from "@/lib/analytics";
import type { pl } from "./pl";

export type Locale = "pl" | "uk" | "ru" | "en";
export type Messages = typeof pl;

export const LOCALES: { id: Locale; name: string; short: string }[] = [
  { id: "pl", name: "Polski", short: "PL" },
  { id: "uk", name: "Українська", short: "UA" },
  { id: "en", name: "English", short: "EN" },
  { id: "ru", name: "Русский", short: "RU" },
];

/** One chunk per language: a reader needs one catalogue of the four, and together they were
 *  more than a third of the code every page had to download before it came alive. */
const LOADERS: Record<Locale, () => Promise<Messages>> = {
  pl: () => import("./pl").then((m) => m.pl),
  uk: () => import("./uk").then((m) => m.uk),
  ru: () => import("./ru").then((m) => m.ru),
  en: () => import("./en").then((m) => m.en),
};
const CATALOGUES: Partial<Record<Locale, Messages>> = {};
const loading: Partial<Record<Locale, Promise<void>>> = {};

/** Fetches a language's catalogue. `main.tsx` awaits the address's language before the first
 *  render (hydration needs the strings on the first pass), and a language switch awaits the
 *  new one before it moves, so no screen is ever drawn without its words. */
export function loadMessages(locale: Locale): Promise<void> {
  if (CATALOGUES[locale]) return Promise.resolve();
  return (loading[locale] ??= LOADERS[locale]().then((messages) => {
    CATALOGUES[locale] = messages;
  }));
}
const STORAGE_KEY = "telc.locale";

/** A dotted path into the catalogue, e.g. `exam.finishBlock`. */
type Path<T> = {
  [K in keyof T & string]: T[K] extends string ? K : `${K}.${Path<T[K]>}`;
}[keyof T & string];

export type MessageKey = Path<Messages>;

function lookup(messages: Messages, key: string): string {
  let node: unknown = messages;
  for (const part of key.split(".")) {
    if (typeof node !== "object" || node === null) return key;
    node = (node as Record<string, unknown>)[part];
  }
  // A missing key shows as the key itself, which is loud enough to notice
  // without breaking the page.
  return typeof node === "string" ? node : key;
}

/**
 * Which of a message's plural forms to use.
 *
 * Polish, Ukrainian and Russian need three ("1 zestaw / 2 zestawy / 5 zestawów"), and
 * getting it wrong is the kind of thing that makes an interface read like a
 * machine translation. A message writes its forms separated by `|`, in the
 * order this table expects, and `t` picks one when a `count` is passed.
 */
const PLURAL_INDEX: Record<Locale, Partial<Record<Intl.LDMLPluralRule, number>>> = {
  pl: { one: 0, few: 1, many: 2, other: 2 },
  uk: { one: 0, few: 1, many: 2, other: 2 },
  ru: { one: 0, few: 1, many: 2, other: 2 },
  en: { one: 0, other: 1 },
};

function selectForm(template: string, locale: Locale, count: number): string {
  const forms = template.split("|");
  if (forms.length === 1) return template;
  const category = new Intl.PluralRules(locale).select(count);
  const index = PLURAL_INDEX[locale][category] ?? forms.length - 1;
  return forms[Math.min(index, forms.length - 1)] ?? forms[0]!;
}

/** Replaces `{name}` placeholders. Values are inserted verbatim. */
function interpolate(
  template: string,
  locale: Locale,
  values?: Record<string, string | number>,
): string {
  const chosen =
    typeof values?.count === "number" ? selectForm(template, locale, values.count) : template;
  if (!values) return chosen;
  return chosen.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in values ? String(values[name]) : whole,
  );
}

/** A language the visitor has picked before, if any. Used only to redirect the
 *  bare `/` — never to override an explicit `/uk/…` or `/en/…` URL. */
function storedLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "pl" || stored === "uk" || stored === "ru" || stored === "en" ? stored : null;
  } catch {
    return null;
  }
}

export type Translate = (key: MessageKey, values?: Record<string, string | number>) => string;

interface I18nValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translate;
  messages: Messages;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  // The URL is the single source of truth for language: `/uk/telc` is
  // Ukrainian, full stop. `useUrlLocale` re-renders this provider whenever the
  // prefix changes, so a language switch is just a navigation.
  const locale = useUrlLocale() as Locale;

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // A returning visitor who once chose Ukrainian or English should not land on
  // the Polish home page. Only the exact `/` is redirected, and only from a
  // stored choice — never by sniffing the browser, which would send crawlers
  // (and their `x-default`) off the Polish page.
  useEffect(() => {
    if (window.location.pathname !== "/") return;
    const pref = storedLocale();
    if (pref && pref !== "pl") void loadMessages(pref).then(() => goTo(localize("/", pref), true));
  }, []);

  const setLocale = useCallback((next: Locale) => {
    // The catalogue first: a screen switched to a language it has no words for is a screen of keys.
    void loadMessages(next).then(() => {
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* private mode; the choice simply will not persist */
      }
      const from = currentLocale();
      goTo(localize(currentPath(), next));
      // After the move, so the event's own `language` is the one chosen.
      if (from !== next) track("language_change", { from_language: from });
    });
  }, []);

  // Normally already in hand (`main.tsx` waits for it); this is the back button into a
  // language that was never opened in this tab.
  const [, loaded] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!CATALOGUES[locale]) void loadMessages(locale).then(loaded);
  }, [locale]);

  const messages = CATALOGUES[locale];
  const value = useMemo<I18nValue | null>(
    () =>
      messages
        ? {
            locale,
            setLocale,
            messages,
            t: (key, values) => interpolate(lookup(messages, key), locale, values),
          }
        : null,
    [locale, setLocale, messages],
  );

  if (!value) return null;
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n used outside I18nProvider");
  return value;
}

export const useT = (): Translate => useI18n().t;

/** Formats a date in the active interface language. */
export function useDateFormat(): (ms: number) => string {
  const { locale } = useI18n();
  return useCallback(
    (ms: number) =>
      new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(ms),
    [locale],
  );
}
